import { useEffect, useMemo, useState } from "react";
import { getAllUsersByAccount } from "../services/user.service";
import { getTabShares, getQuestionShares } from "../services/custom-tab-sharing.service";

const EMPTY_LIST = [];

const getRecipientName = (recipient) => {
	if (!recipient) return "";
	const firstName = recipient.firstName || "";
	const lastName = recipient.lastName || "";
	const fullName = `${firstName} ${lastName}`.trim();
	return fullName || recipient.fullName || recipient.email || "Unknown user";
};

const buildSignature = (items) =>
	(items || [])
		.map((item) => `${item?._id || ""}:${Boolean(item?.isUniversalAccess)}:${Boolean(item?.isUniversal)}`)
		.join("|");

const loadSharesForIds = async (ids, fetchShares, itemType) => {
	if (!ids.length) return [];
	const allUsers = await getAllUsersByAccount({ itemType });
	const usersById = new Map((allUsers || []).map((user) => [String(user._id || user.id), user]));
	const allUsersCount = (allUsers || []).length;

	return Promise.all(
		ids.map(async (id) => {
			try {
				const response = await fetchShares(id);
				const shares = response?.data?.shares || response?.data?.data?.shares || [];
				const recipients = shares
					.filter((share) => !share?.isAutoIncluded)
					.map((share) => {
						const rawRecipient = share?.recipientId;
						const recipientId = typeof rawRecipient === "string" ? rawRecipient : rawRecipient?._id;
						const resolvedRecipient = recipientId ? usersById.get(String(recipientId)) : rawRecipient;
						return getRecipientName(resolvedRecipient);
					})
					.filter(Boolean);
				const uniqueRecipients = [...new Set(recipients)];
				const isSharedWithEveryoneByCount = allUsersCount > 0 && uniqueRecipients.length >= allUsersCount;
				return [id, uniqueRecipients, isSharedWithEveryoneByCount];
			} catch {
				return [id, [], false];
			}
		})
	);
};

/**
 * Loads recipient display names for shared custom tabs and shared dynamic (custom) questions.
 * @param {object[]} customTabs
 * @param {object[]} [dynamicQuestions] — same list as Redux `dynamicCards` / `useAdditionalData` source
 */
const useSharedUsersByCustomTab = (customTabs, dynamicQuestions = EMPTY_LIST) => {
	const [sharedUsersByTabId, setSharedUsersByTabId] = useState({});
	const [sharedWithEveryoneByTabId, setSharedWithEveryoneByTabId] = useState({});
	const tabs = Array.isArray(customTabs) ? customTabs : EMPTY_LIST;
	const questions = Array.isArray(dynamicQuestions) ? dynamicQuestions : EMPTY_LIST;

	const tabsSignature = useMemo(() => buildSignature(tabs), [tabs]);
	const questionsSignature = useMemo(
		() =>
			questions
				.filter((q) => !q?.isDefaultCard)
				.map((q) => `${q?._id || ""}:${Boolean(q?.isUniversalAccess)}:${Boolean(q?.isUniversal)}`)
				.join("|"),
		[questions]
	);

	const combinedSignature = `${tabsSignature}::${questionsSignature}`;

	useEffect(() => {
		let isCancelled = false;

		const loadSharedUsers = async () => {
			const tabIds = tabs
				.filter((tab) => Boolean(tab?._id) && !tab?.isUniversalAccess && !tab?.isUniversal)
				.map((tab) => String(tab._id));

			const questionIds = questions
				.filter((q) => !q?.isDefaultCard && Boolean(q?._id) && !q?.isUniversalAccess && !q?.isUniversal)
				.map((q) => String(q._id));

			if (tabIds.length === 0 && questionIds.length === 0) {
				if (!isCancelled) {
					setSharedUsersByTabId((prev) => (Object.keys(prev).length > 0 ? {} : prev));
					setSharedWithEveryoneByTabId((prev) => (Object.keys(prev).length > 0 ? {} : prev));
				}
				return;
			}

			try {
				const [tabResults, questionResults] = await Promise.all([
					loadSharesForIds(tabIds, getTabShares, "CUSTOM_TAB"),
					loadSharesForIds(questionIds, getQuestionShares, "CUSTOM_QUESTION"),
				]);

				const merged = [...tabResults, ...questionResults];

				if (!isCancelled) {
					const nextSharedUsersByTabId = Object.fromEntries(merged.map(([id, users]) => [id, users]));
					const nextSharedWithEveryoneByTabId = Object.fromEntries(
						merged.map(([id, _users, isSharedWithEveryone]) => [id, isSharedWithEveryone])
					);

					setSharedUsersByTabId((prev) => {
						const same = JSON.stringify(prev) === JSON.stringify(nextSharedUsersByTabId);
						return same ? prev : nextSharedUsersByTabId;
					});
					setSharedWithEveryoneByTabId((prev) => {
						const same = JSON.stringify(prev) === JSON.stringify(nextSharedWithEveryoneByTabId);
						return same ? prev : nextSharedWithEveryoneByTabId;
					});
				}
			} catch {
				if (!isCancelled) {
					setSharedUsersByTabId({});
					setSharedWithEveryoneByTabId({});
				}
			}
		};

		loadSharedUsers();

		return () => {
			isCancelled = true;
		};
	}, [combinedSignature, tabs, questions]);

	return {
		sharedUsersByTabId,
		sharedWithEveryoneByTabId,
	};
};

export default useSharedUsersByCustomTab;
